import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { useGeolocated } from 'react-geolocated';

interface GeolocationContextValue {
  coords: GeolocationCoordinates | undefined;
  isGeolocationAvailable: boolean | undefined;
  isGeolocationEnabled: boolean | undefined;
}

const GeolocationContext = createContext<GeolocationContextValue | null>(null);

export const GeolocationProvider = ({ children }: { children: ReactNode }) => {
  const [reinitKey, remount] = useReducer((n: number) => n + 1, 0);

  useEffect(() => {
    if (!navigator.permissions) return;
    const controller = new AbortController();

    navigator.permissions
      .query({ name: 'geolocation' as PermissionName })
      .then((status) => {
        status.addEventListener('change', remount, {
          signal: controller.signal,
        });
      })
      .catch(() => {
        // Permissions API not queryable — silently no-op.
      });

    return () => controller.abort();
  }, []);

  return <GeolocationInner key={reinitKey}>{children}</GeolocationInner>;
};

const GeolocationInner = ({ children }: { children: ReactNode }) => {
  const { coords, isGeolocationAvailable, isGeolocationEnabled } =
    useGeolocated({
      positionOptions: { enableHighAccuracy: false },
      watchPosition: true,
    });

  const value = useMemo(
    () => ({ coords, isGeolocationAvailable, isGeolocationEnabled }),
    [coords, isGeolocationAvailable, isGeolocationEnabled],
  );

  return (
    <GeolocationContext.Provider value={value}>
      {children}
    </GeolocationContext.Provider>
  );
};

export const useGeolocation = (): GeolocationContextValue => {
  const ctx = useContext(GeolocationContext);
  if (!ctx) {
    throw new Error(
      'useGeolocation must be used within a <GeolocationProvider>',
    );
  }
  return ctx;
};
